import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { BlogPost, PostInput, PostListResponse } from '@educa-legal/shared';
import { api } from '../lib/api';

const POSTS_KEY = ['posts'] as const;

/** Listagem pública paginada (scroll infinito). */
export function useInfinitePosts(limit = 9) {
  return useInfiniteQuery({
    queryKey: [...POSTS_KEY, 'list', limit],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);
      return api.get<PostListResponse>(`/blog/posts?${params.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Os N artigos mais recentes (usado na home). */
export function useLatestPosts(limit = 3) {
  return useQuery({
    queryKey: [...POSTS_KEY, 'latest', limit],
    queryFn: async () => {
      const res = await api.get<PostListResponse>(`/blog/posts?limit=${limit}`);
      return res.items;
    },
  });
}

/** Detalhe por slug — para a página de artigo. */
export function usePost(slug: string | undefined) {
  return useQuery({
    queryKey: [...POSTS_KEY, 'slug', slug],
    queryFn: () => api.get<BlogPost>(`/blog/posts/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
  });
}

/** Listagem completa (rascunhos + publicados) — só admin. */
export function useAdminPosts() {
  return useQuery({
    queryKey: [...POSTS_KEY, 'admin', 'all'],
    queryFn: async () => {
      const res = await api.get<{ items: BlogPost[] }>('/blog/admin/posts');
      return res.items;
    },
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PostInput) => api.post<BlogPost>('/blog/posts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEY }),
  });
}

export function useUpdatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PostInput }) =>
      api.put<BlogPost>(`/blog/posts/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEY }),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/blog/posts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEY }),
  });
}
